import { XYCoord } from "./xy-coord"

export class DNGTemperature {
	public temperature: number = 0
	public tint: number = 0

	private static tintScale = -3000.0

	private static tempTable: readonly { r: number; u: number; v: number; t: number }[] = [
		{ r: 0, u: 0.18006, v: 0.26352, t: -0.24341 },
		{ r: 10, u: 0.18066, v: 0.26589, t: -0.25479 },
		{ r: 20, u: 0.18133, v: 0.26846, t: -0.26876 },
		{ r: 30, u: 0.18208, v: 0.27119, t: -0.28539 },
		{ r: 40, u: 0.18293, v: 0.27407, t: -0.3047 },
		{ r: 50, u: 0.18388, v: 0.27709, t: -0.32675 },
		{ r: 60, u: 0.18494, v: 0.28021, t: -0.35156 },
		{ r: 70, u: 0.18611, v: 0.28342, t: -0.37915 },
		{ r: 80, u: 0.1874, v: 0.28668, t: -0.40955 },
		{ r: 90, u: 0.1888, v: 0.28997, t: -0.44278 },
		{ r: 100, u: 0.19032, v: 0.29326, t: -0.47888 },
		{ r: 125, u: 0.19462, v: 0.30141, t: -0.58204 },
		{ r: 150, u: 0.19962, v: 0.30921, t: -0.70471 },
		{ r: 175, u: 0.20525, v: 0.31647, t: -0.84901 },
		{ r: 200, u: 0.21142, v: 0.32312, t: -1.0182 },
		{ r: 225, u: 0.21807, v: 0.32909, t: -1.2168 },
		{ r: 250, u: 0.22511, v: 0.33439, t: -1.4512 },
		{ r: 275, u: 0.23247, v: 0.33904, t: -1.7298 },
		{ r: 300, u: 0.2401, v: 0.34308, t: -2.0637 },
		{ r: 325, u: 0.24702, v: 0.34655, t: -2.4681 },
		{ r: 350, u: 0.25591, v: 0.34951, t: -2.9641 },
		{ r: 375, u: 0.264, v: 0.352, t: -3.5814 },
		{ r: 400, u: 0.27218, v: 0.35407, t: -4.3633 },
		{ r: 425, u: 0.28039, v: 0.35577, t: -5.3762 },
		{ r: 450, u: 0.28863, v: 0.35714, t: -6.7262 },
		{ r: 475, u: 0.29685, v: 0.35823, t: -8.5955 },
		{ r: 500, u: 0.30505, v: 0.35907, t: -11.324 },
		{ r: 525, u: 0.3132, v: 0.35968, t: -15.628 },
		{ r: 550, u: 0.32129, v: 0.36011, t: -23.325 },
		{ r: 575, u: 0.32931, v: 0.36038, t: -40.77 },
		{ r: 600, u: 0.33724, v: 0.36051, t: -116.45 },
	] as const

	constructor()
	constructor(aTemperature: number, aTint: number)
	constructor(xy: XYCoord)
	constructor(arg1?: number | XYCoord, arg2?: number) {
		if (typeof arg1 === "number" && typeof arg2 === "number") {
			this.temperature = arg1
			this.tint = arg2
		} else if (arg1 instanceof XYCoord) {
			this.XYCoord = arg1
		} else {
			this.temperature = 0
			this.tint = 0
		}
	}

	get XYCoord(): XYCoord {
		const result = new XYCoord()

		const r = 1.0e6 / this.temperature

		const offset = this.tint * (1.0 / DNGTemperature.tintScale)
		for (let index = 0; index <= 29; index++) {
			if (r < this.readTempTable(index + 1).r || index === 29) {
				const f =
					(this.readTempTable(index + 1).r - r) /
					(this.readTempTable(index + 1).r - this.readTempTable(index).r)

				let u =
					this.readTempTable(index).u * f + this.readTempTable(index + 1).u * (1.0 - f)

				let v =
					this.readTempTable(index).v * f + this.readTempTable(index + 1).v * (1.0 - f)

				let uu1 = 1.0
				let vv1 = this.readTempTable(index).t

				let uu2 = 1.0
				let vv2 = this.readTempTable(index + 1).t

				const len1 = Math.sqrt(1.0 + vv1 * vv1)
				const len2 = Math.sqrt(1.0 + vv2 * vv2)

				uu1 /= len1
				vv1 /= len1

				uu2 /= len2
				vv2 /= len2

				let uu3 = uu1 * f + uu2 * (1.0 - f)
				let vv3 = vv1 * f + vv2 * (1.0 - f)

				const len3 = Math.sqrt(uu3 * uu3 + vv3 * vv3)

				uu3 /= len3
				vv3 /= len3

				u += uu3 * offset
				v += vv3 * offset

				result.x = (1.5 * u) / (u - 4.0 * v + 2.0)
				result.y = v / (u - 4.0 * v + 2.0)

				break
			}
		}

		return result
	}

	set XYCoord(value: XYCoord) {
		const u = (2.0 * value.x) / (1.5 - value.x + 6.0 * value.y)
		const v = (3.0 * value.y) / (1.5 - value.x + 6.0 * value.y)

		let last_dt = 0.0
		let last_dv = 0.0
		let last_du = 0.0

		for (let index = 1; index <= 30; index++) {
			let du = 1.0
			let dv = this.readTempTable(index).t

			let len = Math.sqrt(1.0 + dv * dv)
			du /= len
			dv /= len

			let uu = u - this.readTempTable(index).u
			let vv = v - this.readTempTable(index).v

			let dt = -uu * dv + vv * du

			if (dt <= 0.0 || index === 30) {
				if (dt > 0.0) dt = 0.0
				dt = -dt

				let f: number

				if (index === 1) {
					f = 0.0
				} else {
					f = dt / (last_dt + dt)
				}

				this.temperature =
					1.0e6 /
					(this.readTempTable(index - 1).r * f + this.readTempTable(index).r * (1.0 - f))

				uu =
					u -
					(this.readTempTable(index - 1).u * f + this.readTempTable(index).u * (1.0 - f))

				vv =
					v -
					(this.readTempTable(index - 1).v * f + this.readTempTable(index).v * (1.0 - f))

				du = du * (1.0 - f) + last_du * f
				dv = dv * (1.0 - f) + last_dv * f

				len = Math.sqrt(du * du + dv * dv)

				du /= len
				dv /= len

				this.tint = (uu * du + vv * dv) * DNGTemperature.tintScale

				break
			}

			last_dt = dt
			last_du = du
			last_dv = dv
		}
	}

	private readTempTable(index: number): { r: number; u: number; v: number; t: number } {
		const value = DNGTemperature.tempTable[index]

		if (!value) {
			return { r: 0, u: 0, v: 0, t: 0 }
		}

		return value
	}
}
